import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  Plus, Clock, AlertCircle, CheckCircle2, HandHeart,
  Search, Phone, Building2, Mail, Trash2,
} from 'lucide-react';
import { formatDateTime } from '../utils/dateFormat';

import { API } from '../lib/apiBase';

const URGENCY_META = {
  high: { label: 'High', color: 'bg-[#FDECEC] text-[#B91C1C] border-[#F5C6C6]' },
  medium: { label: 'Medium', color: 'bg-[#FFF8E1] text-[#B45309] border-[#FCD9A1]' },
  low: { label: 'Low', color: 'bg-[#E9EFEA] text-[#4A5D4E] border-[#CDD9CF]' },
};

const STATUS_META = {
  open: { label: 'Open', color: 'bg-[#E0F2FE] text-[#0369A1] border-[#BAE6FD]', icon: AlertCircle },
  in_progress: { label: 'In progress', color: 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]', icon: Clock },
  handled: { label: 'Handled', color: 'bg-[#DCFCE7] text-[#166534] border-[#86EFAC]', icon: CheckCircle2 },
};

export default function WaitingListPage() {
  const { organization, isOperator } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const dateFmt = organization?.date_format || 'DD/MM/YYYY';
  const timeFmt = organization?.time_format || '24h';

  const [form, setForm] = useState({
    beneficiary_name: '',
    phone_number: '',
    need_description: '',
    urgency: 'medium',
    notes: '',
  });

  const fetchEntries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/waiting-list`);
      setEntries(res.data);
    } catch {
      toast.error('Error loading waiting list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleCreate = async () => {
    if (!form.beneficiary_name.trim() || !form.need_description.trim()) {
      toast.error('Name and need description are required');
      return;
    }
    try {
      await axios.post(`${API}/waiting-list`, form);
      toast.success('Added to waiting list — visible to all organizations');
      setShowAdd(false);
      setForm({ beneficiary_name: '', phone_number: '', need_description: '', urgency: 'medium', notes: '' });
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error creating entry');
    }
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(`${API}/waiting-list/${id}`, { status: newStatus });
      const messages = {
        in_progress: 'You took this request in charge',
        handled: 'Marked as handled',
        open: 'Reopened',
      };
      toast.success(messages[newStatus] || 'Updated');
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cannot update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry? Only your organization can delete its own entries.')) return;
    try {
      await axios.delete(`${API}/waiting-list/${id}`);
      toast.success('Entry deleted');
      fetchEntries();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cannot delete');
    }
  };

  const filtered = entries.filter(e => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return (
        e.beneficiary_name?.toLowerCase().includes(s) ||
        e.organization_name?.toLowerCase().includes(s) ||
        e.need_description?.toLowerCase().includes(s) ||
        e.phone_number?.includes(search)
      );
    }
    return true;
  });

  const counts = {
    open: entries.filter(e => e.status === 'open').length,
    in_progress: entries.filter(e => e.status === 'in_progress').length,
    handled: entries.filter(e => e.status === 'handled').length,
  };

  return (
    <div className="space-y-6" data-testid="waiting-list-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] font-['Manrope']">Shared Waiting List</h1>
          <p className="text-[#595959] mt-1 text-sm md:text-base">
            Beneficiaries waiting for help — shared across all organizations.
          </p>
        </div>
        {isOperator && (
          <Button
            className="bg-[#4A5D4E] hover:bg-[#3D4D40] w-full md:w-auto"
            onClick={() => setShowAdd(true)}
            data-testid="add-waiting-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add to waiting list
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        {Object.entries(counts).map(([key, val]) => {
          const meta = STATUS_META[key];
          const Icon = meta.icon;
          return (
            <Card key={key} className="border-[#E5E5E5]">
              <CardContent className="p-3 md:p-4">
                <div className="flex items-center gap-2 md:gap-3">
                  <div className={`w-9 h-9 md:w-10 md:h-10 rounded-lg flex items-center justify-center ${meta.color}`}>
                    <Icon className="w-4 h-4 md:w-5 md:h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] md:text-xs text-[#8C8C8C] truncate">{meta.label}</p>
                    <p className="text-lg md:text-2xl font-bold text-[#1A1A1A]">{val}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-2 md:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
          <Input
            placeholder="Search by name, organization, need..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="waiting-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-[180px]" data-testid="waiting-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In progress</SelectItem>
            <SelectItem value="handled">Handled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* List */}
      {loading ? (
        <Card className="border-[#E5E5E5]"><CardContent className="py-8 text-center text-[#8C8C8C]">Loading...</CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card className="border-[#E5E5E5]">
          <CardContent className="py-12 text-center text-[#8C8C8C]">
            <HandHeart className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No entries in the waiting list</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((e) => {
            const urgency = URGENCY_META[e.urgency] || URGENCY_META.medium;
            const status = STATUS_META[e.status] || STATUS_META.open;
            const StatusIcon = status.icon;
            return (
              <Card
                key={e.id}
                className={`border ${e.is_mine ? 'border-[#4A5D4E]' : 'border-[#E5E5E5]'}`}
                data-testid={`waiting-row-${e.id}`}
              >
                <CardContent className="p-4 md:p-5">
                  <div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="font-semibold text-[#1A1A1A] text-base md:text-lg break-words">
                          {e.beneficiary_name}
                        </h3>
                        <Badge variant="outline" className={urgency.color}>{urgency.label}</Badge>
                        <Badge variant="outline" className={status.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />{status.label}
                        </Badge>
                        {e.is_mine && (
                          <Badge variant="outline" className="bg-[#E9EFEA] text-[#4A5D4E] border-[#CDD9CF]">
                            Your org
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-[#595959] mb-2 break-words">{e.need_description}</p>
                      {e.notes && (
                        <p className="text-xs text-[#8C8C8C] italic mb-2 break-words">"{e.notes}"</p>
                      )}
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[#595959]">
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3 h-3" /> {e.organization_name}
                        </span>
                        {e.phone_number && (
                          <a href={`tel:${e.phone_number}`} className="flex items-center gap-1 hover:text-[#4A5D4E]">
                            <Phone className="w-3 h-3" /> {e.phone_number}
                          </a>
                        )}
                        {e.organization_email && (
                          <a href={`mailto:${e.organization_email}`} className="flex items-center gap-1 hover:text-[#4A5D4E]">
                            <Mail className="w-3 h-3" /> {e.organization_email}
                          </a>
                        )}
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {formatDateTime(e.created_at, dateFmt, timeFmt)}
                        </span>
                        {e.handled_by_organization_name && (
                          <span className="flex items-center gap-1 text-[#92400E]">
                            <HandHeart className="w-3 h-3" /> Taken by {e.handled_by_organization_name}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-row md:flex-col gap-2 md:min-w-[160px] flex-shrink-0">
                      {e.status === 'open' && (
                        <Button
                          size="sm"
                          className="bg-[#4A5D4E] hover:bg-[#3D4D40] flex-1 md:flex-none"
                          onClick={() => updateStatus(e.id, 'in_progress')}
                          data-testid={`take-charge-${e.id}`}
                        >
                          <HandHeart className="w-3.5 h-3.5 mr-1.5" />
                          Take charge
                        </Button>
                      )}
                      {e.status === 'in_progress' && (e.is_handled_by_mine || e.is_mine) && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#166534] text-[#166534] hover:bg-[#DCFCE7] flex-1 md:flex-none"
                            onClick={() => updateStatus(e.id, 'handled')}
                            data-testid={`mark-handled-${e.id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
                            Mark handled
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[#8C8C8C]"
                            onClick={() => updateStatus(e.id, 'open')}
                          >
                            Release
                          </Button>
                        </>
                      )}
                      {e.status === 'handled' && (e.is_mine || e.is_handled_by_mine) && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[#8C8C8C]"
                          onClick={() => updateStatus(e.id, 'open')}
                        >
                          Reopen
                        </Button>
                      )}
                      {e.is_mine && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-[#D34848] hover:bg-[#FDECEC]"
                          onClick={() => handleDelete(e.id)}
                          data-testid={`delete-waiting-${e.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Add Modal */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope']">Add to waiting list</DialogTitle>
            <DialogDescription>
              This entry will be visible to all organizations on ATHINAPPY.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Beneficiary name *</Label>
              <Input
                value={form.beneficiary_name}
                onChange={(e) => setForm({ ...form, beneficiary_name: e.target.value })}
                placeholder="Full name"
                data-testid="waiting-name-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Phone (optional)</Label>
              <Input
                value={form.phone_number}
                onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                placeholder="+39..."
                data-testid="waiting-phone-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Need description *</Label>
              <Textarea
                value={form.need_description}
                onChange={(e) => setForm({ ...form, need_description: e.target.value })}
                placeholder="What kind of help is needed?"
                rows={2}
                data-testid="waiting-need-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label>Urgency</Label>
              <Select value={form.urgency} onValueChange={(v) => setForm({ ...form, urgency: v })}>
                <SelectTrigger data-testid="waiting-urgency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button className="bg-[#4A5D4E] hover:bg-[#3D4D40]" onClick={handleCreate} data-testid="save-waiting-button">
              Add to list
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
